import React from 'react';

import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';

import Table from 'components/application/Table';

import TrainingRow from 'components/mobileTrainingProfile/components/homeScreen/TrainingRow';

import { sortAlphabetically, sortByDate } from 'components/helpers/arrays';
import { personnelCourseConnectionsRequirements } from 'components/helpers/resources/courses';
import { courseTrainingStatusOrdering } from 'components/helpers/resources/training';
import { resourceShape } from 'components/helpers/serialisableResources';

export default function RoleTrainingTable({
  attachmentsGroupedByTrainingId,
  bookingsIndexedByCourseId,
  courseCompanyRolesIndexedByCourseId,
  courses,
  onViewCourseDetailsClick,
  registrationsIndexedByCourseId,
  trainingGroupedByCourseId,
  userCoursesIndexedByCourseId,
}) {
  const { t: i18nTShared } = useTranslation('common', {
    keyPrefix: 'shared',
  });

  const rows = courses.reduce((acc, course) => {
    const latestTraining =
      (trainingGroupedByCourseId[course.id] &&
        trainingGroupedByCourseId[course.id][0]) ||
      null;
    const attachments =
      attachmentsGroupedByTrainingId[latestTraining?.id] || [];
    const booking = bookingsIndexedByCourseId[course.id];
    const registration = registrationsIndexedByCourseId[course.id];

    const userCourse = userCoursesIndexedByCourseId[course.id];
    const courseCompanyRole = courseCompanyRolesIndexedByCourseId[course.id];
    const { isRequired, isRequiredByUserCourse } =
      personnelCourseConnectionsRequirements({
        courseCompanyRole,
        isRequirementPriorityPresent:
          !!userCourse?.relationships
            .personnelCourseConnectionRequirementPriority.data,
        userCourse,
      });

    let requiredSource;
    if (isRequiredByUserCourse) {
      requiredSource = 'UserCourse';
    } else if (isRequired) {
      requiredSource = 'Role';
    }

    const orderingBand = courseTrainingStatusOrdering({
      course,
      isRequired,
      training: latestTraining,
    });

    acc.push({
      course,
      latestTraining,
      attachments,
      booking,
      registration,
      isRequired,
      requiredSource,
      orderingBand,
    });
    return acc;
  }, []);

  const orderedRows = rows.sort((a, b) => {
    const sortByOrderingBand = (a, b) => a.orderingBand - b.orderingBand;
    const orderingSort = sortByOrderingBand(a, b);
    if (orderingSort !== 0) {
      return orderingSort;
    }
    const dateSort = sortByDate(
      a.latestTraining && a.latestTraining.attributes.expiryDate,
      b.latestTraining && b.latestTraining.attributes.expiryDate,
    );
    if (dateSort !== 0) {
      return dateSort;
    }
    return sortAlphabetically(
      a.course.attributes.name,
      b.course.attributes.name,
    );
  });

  return (
    <Table
      headers={
        <tr>
          <th className='tw-px-9 tw-text-s tw-font-medium tw-tracking-wide tw-text-grey-900'>
            {i18nTShared('courses')}
          </th>
        </tr>
      }
      rows={orderedRows.map((row) => (
        <TrainingRow
          attachments={row.attachments}
          booking={row.booking}
          course={row.course}
          isRequired={row.isRequired}
          key={`roleCourseTraining--${row.course.id}`}
          latestTraining={row.latestTraining}
          onViewCourseDetailsClick={onViewCourseDetailsClick}
          registration={row.registration}
          requiredSource={row.requiredSource}
        />
      ))}
      tableClassName='tw-border-t-0'
    />
  );
}

RoleTrainingTable.propTypes = {
  courses: PropTypes.arrayOf(resourceShape('course')).isRequired,
  userCoursesIndexedByCourseId: PropTypes.object.isRequired,
  courseCompanyRolesIndexedByCourseId: PropTypes.object.isRequired,
  bookingsIndexedByCourseId: PropTypes.object.isRequired,
  registrationsIndexedByCourseId: PropTypes.object.isRequired,
  trainingGroupedByCourseId: PropTypes.object.isRequired,
  attachmentsGroupedByTrainingId: PropTypes.object.isRequired,
  onViewCourseDetailsClick: PropTypes.func.isRequired,
};
